import { useState, useEffect } from "react";
import { usePermissions } from "./PermissionsContext";
import { fmt } from "./Utils/Utilities";
import { Badge } from "./SharedUI";
import "./Main.css";
import "./DashBoard.css";

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE = "https://api.seritonex.com";
const token = () => localStorage.getItem("token");

const decodeToken = () => {
  try {
    const jwt = token();
    if (!jwt) return null;
    const base64 = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

const organizationId = () => {
  const payload = decodeToken();
  return parseInt(payload?.organization?.organization_id ?? 1, 10);
};

const getCurrentUserId = () => {
  const payload = decodeToken();
  if (!payload) return null;
  const raw = payload.user_id ?? payload.id ?? payload.sub ?? null;
  if (raw == null) return null;
  const n = Number(raw);
  return !isNaN(n) && n > 0 ? n : null;
};

const apiFetch = async (path, body) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `${res.status}: ${res.statusText}`);
  }
  return res.json();
};

const apiGet = async (path) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      Authorization: `Bearer ${token()}`,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `${res.status}: ${res.statusText}`);
  }
  return res.json();
};

// ─── API calls ────────────────────────────────────────────────────────────────

const fetchRecentOrders = () =>
  apiFetch("/api/v1/amis/purchase-orders/all", {
    organization_id: organizationId(),
    is_supplier: false,
  },[]);

const fetchAllWarehouses = () => apiGet("/api/v1/amis/warehouse/get-all");

const fetchActiveCollections = () =>
  apiFetch("/api/v1/amis/collection/list", {
    organization_id: organizationId(),
    is_supplier: false,
  },[]);

const fetchBranchesByUser = (user_id) =>
  apiFetch("/api/v1/amis/branch/by-user-id", { user_id },[]);

const fetchTransitsByBranch = (branch_id) =>
  apiFetch("/api/v1/amis/transit/open-by-branch", {
    branch_id: parseInt(branch_id, 10),
  },[]);

const fetchWarehousesByBranch = (branch_id) =>
  apiFetch("/api/v1/amis/warehouse/get-by-branch-id", {
    branch_id: parseInt(branch_id, 10),
  },[]);

const fetchAllocationSummary = (
  isSupplier = false,
  warehouseId = null,
  branchId = null,
) => {
  const body = { is_supplier: isSupplier };
  if (warehouseId != null) body.warehouse_id = warehouseId;
  if (branchId != null) body.branch_id = branchId;
  return apiFetch("/api/v1/amis/asset/current-allocation-summary", body);
};

// ─── Donut colours ────────────────────────────────────────────────────────────
const COLORS = [
  "#4F8EF7",
  "#FF6B3D",
  "#F5C842",
  "#34C78A",
  "#A78BFA",
  "#F97316",
  "#06B6D4",
  "#EC4899",
  "#84CC16",
  "#F43F5E",
];

// ─── Transit normaliser ───────────────────────────────────────────────────────
const normalizeTransit = (t) => {
  const allAssets = Array.isArray(t.assets) ? t.assets : [];
  const validAssets = allAssets.filter(
    (a) => a.asset_id != null && a.asset_id !== 0,
  );
  const damagedCount = allAssets.filter(
    (a) => a.asset_condition?.description === "Damaged",
  ).length;
  return {
    id: t.transit_id,
    transitNumber: t.transit_number ?? "-",
    vehicle: t.vehicle_registration_number ?? "—",
    dispatchDate: t.dispatch_date ?? null,
    type: t.transit_type?.description ?? "-",
    status: t.transit_status?.description ?? "Unknown",
    assetCount: validAssets.length,
    damagedCount,
    notes: t.notes ?? "",
    branchName: "",
  };
};

// ─── Permission blur wrapper ──────────────────────────────────────────────────

function PermBlur({ allowed, children }) {
  return (
    <div style={{ position: "relative" }}>
      {/* Blurred layer */}
      <div
        style={{
          filter: allowed ? "none" : "blur(6px)",
          pointerEvents: allowed ? "auto" : "none",
          userSelect: allowed ? "auto" : "none",
          transition: "filter 0.2s ease",
          ...(allowed ? {} : { overflow: "hidden", borderRadius: 8 }),
        }}
      >
        {children}
      </div>

      {/* Lock overlay — only mounted when not allowed */}
      {!allowed && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            borderRadius: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              padding: "10px 20px",
              background: "var(--surface1)",
              borderRadius: 8,
              border: "1px solid var(--border)",
              boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
            }}
          >
            <i
              className="bi bi-lock-fill"
              style={{ fontSize: 20, color: "var(--text3)" }}
            />
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "var(--text3)",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
              }}
            >
              Access restricted
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Dashboard ──────────────────────────────────────────────────────────────
function DashboardPage() {
  // ── Recent Orders ─────────────────────────────────────────────────────────
  const [recentOrders, setRecentOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await fetchRecentOrders();
        if (!cancelled) {
          setRecentOrders(
            (Array.isArray(raw) ? raw : []).slice(0, 5).map((o) => ({
              id: o.purchase_order_number ?? String(o.purchase_order_id),
              site: o.customer_branch?.branch_name ?? "—",
              date: o.order_date,
              total: o.total ?? null,
              status: o.status?.description ?? "Pending",
            })),
          );
        }
      } catch {
        if (!cancelled) setRecentOrders([]);
      } finally {
        if (!cancelled) setOrdersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Assets by Warehouse donut ──────────────────────────────────────────────
  const [warehouseSlices, setWarehouseSlices] = useState([]);
  const [warehouseLoading, setWarehouseLoading] = useState(true);
  const [totalAssets, setTotalAssets] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const whRaw = await fetchAllWarehouses();
        const warehouses = Array.isArray(whRaw)
          ? whRaw
          : (whRaw?.warehouses ?? whRaw?.data ?? whRaw?.items ?? []);

        if (warehouses.length === 0) {
          if (!cancelled) setWarehouseLoading(false);
          return;
        }

        const slices = warehouses
          .map((w, idx) => ({
            name: w.warehouse_name ?? `Warehouse ${w.warehouse_id ?? idx}`,
            count: w.current_inventory ?? 0,
            color: COLORS[idx % COLORS.length],
          }))
          .sort((a, b) => b.count - a.count);

        const total = slices.reduce((s, i) => s + i.count, 0);

        if (!cancelled) {
          setTotalAssets(total);
          setWarehouseSlices(
            slices.map((s) => ({
              ...s,
              pct: total > 0 ? Math.round((s.count / total) * 100) : 0,
            })),
          );
        }
      } catch {
        if (!cancelled) {
          setWarehouseSlices([]);
          setTotalAssets(0);
        }
      } finally {
        if (!cancelled) setWarehouseLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Active Collections ─────────────────────────────────────────────────────
  const [activeCollections, setActiveCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await fetchActiveCollections();
        if (!cancelled) {
          setActiveCollections(
            (Array.isArray(raw) ? raw : []).slice(0, 8).map((c) => ({
              id: c.collection_id ?? c.id ?? "—",
              customer:
                c.customer?.name ??
                c.customer_branch?.branch_name ??
                c.customer_name ??
                "—",
              site: c.branch?.branch_name ?? c.site_name ?? c.site ?? "—",
              assetCount: c.asset_count ?? c.qty ?? c.quantity ?? 0,
              status:
                c.collection_status?.description ??
                c.status?.description ??
                c.status ??
                "Active",
              date: c.collection_date ?? c.created_at ?? c.date ?? null,
            })),
          );
        }
      } catch {
        if (!cancelled) setActiveCollections([]);
      } finally {
        if (!cancelled) setCollectionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Active Transits ────────────────────────────────────────────────────────
  const [activeTransits, setActiveTransits] = useState([]);
  const [transitsLoading, setTransitsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const uid = getCurrentUserId();
        if (!uid) {
          if (!cancelled) setTransitsLoading(false);
          return;
        }

        const branchRes = await fetchBranchesByUser(uid);
        let branches = [];
        if (Array.isArray(branchRes)) {
          branches = branchRes;
        } else if (branchRes && typeof branchRes === "object") {
          const b = branchRes.branches;
          if (Array.isArray(b)) branches = b;
          else if (b && typeof b === "object") branches = [b];
          else if (Array.isArray(branchRes.data)) branches = branchRes.data;
          else if (branchRes.branch_id != null || branchRes.id != null)
            branches = [branchRes];
        }

        if (branches.length === 0) {
          if (!cancelled) setTransitsLoading(false);
          return;
        }

        const allTransits = [];
        await Promise.all(
          branches.map(async (b) => {
            const bid = b.branch_id ?? b.id;
            const bName = b.branch_name ?? b.name ?? `Branch ${bid}`;
            try {
              const tRes = await fetchTransitsByBranch(bid);
              const arr = Array.isArray(tRes)
                ? tRes
                : (tRes?.transits ?? tRes?.data ?? []);
              for (const t of arr) {
                const norm = normalizeTransit(t);
                norm.branchName = bName;
                allTransits.push(norm);
              }
            } catch {
              // swallow per-branch errors
            }
          }),
        );

        if (!cancelled) {
          allTransits.sort((a, b) => {
            const da = a.dispatchDate ? new Date(a.dispatchDate) : new Date(0);
            const db = b.dispatchDate ? new Date(b.dispatchDate) : new Date(0);
            return db - da;
          });
          setActiveTransits(allTransits.slice(0, 8));
        }
      } catch {
        if (!cancelled) setActiveTransits([]);
      } finally {
        if (!cancelled) setTransitsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Allocation Summary ─────────────────────────────────────────────────────
  const [allocationRows, setAllocationRows] = useState([]);
  const [allocationLoading, setAllocationLoading] = useState(true);
  const [allocationBranches, setAllocationBranches] = useState([]);
  const [selectedAllocationBranch, setSelectedAllocationBranch] =
    useState(null);
  const [allocationWarehouses, setAllocationWarehouses] = useState([]);
  const [selectedAllocationWarehouse, setSelectedAllocationWarehouse] =
    useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const uid = getCurrentUserId();
        if (!uid) return;

        const branchRes = await fetchBranchesByUser(uid);
        let branches = [];
        if (Array.isArray(branchRes)) {
          branches = branchRes;
        } else if (branchRes && typeof branchRes === "object") {
          const b = branchRes.branches;
          if (Array.isArray(b)) branches = b;
          else if (b && typeof b === "object") branches = [b];
          else if (Array.isArray(branchRes.data)) branches = branchRes.data;
          else if (branchRes.branch_id != null || branchRes.id != null)
            branches = [branchRes];
        }

        if (!cancelled) {
          setAllocationBranches(branches);
          const first = branches[0];
          const firstId = first?.branch_id ?? first?.id ?? null;
          setSelectedAllocationBranch(firstId);
        }
      } catch {
        if (!cancelled) setAllocationBranches([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!selectedAllocationBranch) {
      setAllocationWarehouses([]);
      setSelectedAllocationWarehouse(null);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const raw = await fetchWarehousesByBranch(selectedAllocationBranch);
        let warehouses = [];
        if (Array.isArray(raw)) {
          warehouses = raw;
        } else if (raw && typeof raw === "object") {
          const w = raw.warehouses ?? raw.data ?? raw.items;
          if (Array.isArray(w)) warehouses = w;
          else if (w && typeof w === "object") warehouses = [w];
          else if (raw.warehouse_id != null || raw.id != null)
            warehouses = [raw];
        }

        if (!cancelled) {
          setAllocationWarehouses(warehouses);
          const firstWarehouse = warehouses[0];
          const firstWarehouseId =
            firstWarehouse?.warehouse_id ?? firstWarehouse?.id ?? null;
          setSelectedAllocationWarehouse(firstWarehouseId);
        }
      } catch {
        if (!cancelled) {
          setAllocationWarehouses([]);
          setSelectedAllocationWarehouse(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedAllocationBranch]);

  useEffect(() => {
    let cancelled = false;
    setAllocationLoading(true);
    (async () => {
      try {
        const raw = await fetchAllocationSummary(
          false,
          selectedAllocationWarehouse,
          selectedAllocationBranch,
        );
        if (!cancelled) {
          const list = Array.isArray(raw?.summary)
            ? raw.summary
            : Array.isArray(raw?.data)
              ? raw.data
              : Array.isArray(raw?.result)
                ? raw.result
                : Array.isArray(raw?.allocations)
                  ? raw.allocations
                  : Array.isArray(raw)
                    ? raw
                    : [];
          setAllocationRows(list);
        }
      } catch {
        if (!cancelled) setAllocationRows([]);
      } finally {
        if (!cancelled) setAllocationLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedAllocationBranch, selectedAllocationWarehouse]);

  // ── Permissions ────────────────────────────────────────────────────────────
  const {
    canSeePage,
    canViewAssets,
    canViewTransit,
    canViewOrders,
    canViewCollections,
    ready,
  } = usePermissions();

  // Defer blur until the token has been decoded — avoids a blurred flash on load
  const perm = (flag) => !ready || flag;

  if (ready && !canSeePage("dashboard")) {
    return (
      <div className="panel" style={{ padding: 24 }}>
        <h2>Access denied</h2>
        <p className="tc3">
          You do not have permission to view this dashboard.
        </p>
      </div>
    );
  }

  // ── Donut geometry ─────────────────────────────────────────────────────────
  const r = 54,
    cx = 65,
    cy = 65,
    stroke = 22;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const segments = warehouseSlices.map((s) => {
    const dash = (s.pct / 100) * circ;
    const seg = { ...s, dash, offset };
    offset += dash + 2;
    return seg;
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="page">
      {/* ── Top Row: Donut + Active Transits ── */}
      <div className="g2 dash-charts-row">
        {/* Assets by Warehouse donut — gated on ASSET:READ */}
        <PermBlur allowed={perm(canViewAssets)}>
          <div className="panel dash-panel">
            <div className="ph">
              <span className="ph-title">Assets by Warehouse</span>
              {!warehouseLoading && (
                <span className="ph-sub">
                  {totalAssets} total · {warehouseSlices.length} warehouses
                </span>
              )}
            </div>
            <div
              className="pb dash-panel-body"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 28,
                padding: "16px 20px",
              }}
            >
              {warehouseLoading ? (
                <div
                  style={{
                    padding: "24px 0",
                    textAlign: "center",
                    color: "var(--text3)",
                    fontSize: 12,
                    width: "100%",
                  }}
                >
                  Loading…
                </div>
              ) : warehouseSlices.length === 0 ? (
                <div
                  style={{
                    padding: "24px 0",
                    textAlign: "center",
                    color: "var(--text3)",
                    fontSize: 12,
                    width: "100%",
                  }}
                >
                  No warehouse data
                </div>
              ) : (
                <>
                  <svg width={130} height={130} style={{ flexShrink: 0 }}>
                    <circle
                      cx={cx}
                      cy={cy}
                      r={r}
                      fill="none"
                      stroke="var(--border)"
                      strokeWidth={stroke}
                    />
                    {segments.map((s) => (
                      <circle
                        key={s.name}
                        cx={cx}
                        cy={cy}
                        r={r}
                        fill="none"
                        stroke={s.color}
                        strokeWidth={stroke}
                        strokeDasharray={`${s.dash} ${circ - s.dash}`}
                        strokeDashoffset={-s.offset}
                        style={{
                          transform: "rotate(-90deg)",
                          transformOrigin: `${cx}px ${cy}px`,
                        }}
                      />
                    ))}
                    <text
                      x={cx}
                      y={cy - 6}
                      textAnchor="middle"
                      style={{
                        fontSize: 18,
                        fontWeight: 800,
                        fill: "var(--text1)",
                      }}
                    >
                      {totalAssets}
                    </text>
                    <text
                      x={cx}
                      y={cy + 10}
                      textAnchor="middle"
                      style={{ fontSize: 9, fill: "var(--text3)" }}
                    >
                      assets
                    </text>
                  </svg>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      flex: 1,
                    }}
                  >
                    {warehouseSlices.map((s) => (
                      <div
                        key={s.name}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            borderRadius: 2,
                            background: s.color,
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            flex: 1,
                            fontSize: 12,
                            color: "var(--text1)",
                          }}
                        >
                          {s.name}
                        </span>
                        <span
                          className="mono fw6 fs12"
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {s.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </PermBlur>

        {/* Active Transits — gated on TRANSIT:READ */}
        <PermBlur allowed={perm(canViewTransit)}>
          <div className="panel dash-panel">
            <div className="ph">
              <span className="ph-title">Active Transits</span>
              {!transitsLoading && (
                <span className="ph-sub">
                  {activeTransits.length} open transits
                </span>
              )}
            </div>
            <div className="pb dash-panel-body">
              {transitsLoading ? (
                <div
                  style={{
                    padding: "24px 0",
                    height: "250px",
                    textAlign: "center",
                    color: "var(--text3)",
                    fontSize: 12,
                  }}
                >
                  Loading…
                </div>
              ) : activeTransits.length === 0 ? (
                <div
                  style={{
                    padding: "24px 0",
                    textAlign: "center",
                    color: "var(--text3)",
                    fontSize: 12,
                  }}
                >
                  No active transits
                </div>
              ) : (
                <div className="dash-transit-list">
                  {activeTransits.map((t) => (
                    <div key={t.id} className="dash-transit-row">
                      <div
                        className="dash-transit-type-dot"
                        style={{
                          background:
                            t.type === "Return from Customer"
                              ? "rgba(64,196,255,.15)"
                              : "rgba(29,233,182,.12)",
                          border:
                            t.type === "Return from Customer"
                              ? "1px solid rgba(64,196,255,.35)"
                              : "1px solid rgba(29,233,182,.3)",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            color:
                              t.type === "Return from Customer"
                                ? "var(--blue)"
                                : "var(--teal)",
                          }}
                        >
                          {t.type === "Return from Customer" ? "↩" : "↗"}
                        </span>
                      </div>
                      <div className="dash-transit-info">
                        <div className="dash-transit-number t-flame mono fw6 fs12">
                          {t.transitNumber}
                        </div>
                        <div className="fs10 tc3">
                          {t.vehicle}
                          {t.branchName ? ` · ${t.branchName}` : ""}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0 }}>
                        <Badge status={t.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </PermBlur>
      </div>

      {/* ── Middle Row: Recent Orders + Active Collections ── */}
      <div className="g2 dash-bottom-row">
        {/* Recent Orders — gated on PURCHASE_ORDER:READ */}
        <PermBlur allowed={perm(canViewOrders)}>
          <div className="panel dash-panel">
            <div className="ph">
              <span className="ph-title">Recent Orders</span>
              <span className="ph-sub">Last 5 transactions</span>
            </div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Site</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {ordersLoading ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        textAlign: "center",
                        padding: "18px 0",
                        color: "var(--text3)",
                        fontSize: 12,
                      }}
                    >
                      Loading…
                    </td>
                  </tr>
                ) : recentOrders.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        textAlign: "center",
                        padding: "18px 0",
                        color: "var(--text3)",
                        fontSize: 12,
                      }}
                    >
                      No recent orders
                    </td>
                  </tr>
                ) : (
                  recentOrders.map((o) => (
                    <tr key={o.id}>
                      <td className="mono t-flame">{o.id}</td>
                      <td className="dash-site-cell">{o.site}</td>
                      <td className="fs11 tc2">
                        {o.date ? fmt.date(o.date) : "—"}
                      </td>
                      <td>
                        <Badge status={o.status} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </PermBlur>

        {/* Active Collections — gated on COLLECTION:READ */}
        <PermBlur allowed={perm(canViewCollections)}>
          <div className="panel dash-panel" style={{ height: "330px" }}>
            <div className="ph">
              <span className="ph-title">Active Collections</span>
              {!collectionsLoading && (
                <span className="ph-sub">
                  {activeCollections.length} records
                </span>
              )}
            </div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {collectionsLoading ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        textAlign: "center",
                        padding: "18px 0",
                        color: "var(--text3)",
                        fontSize: 12,
                      }}
                    >
                      Loading…
                    </td>
                  </tr>
                ) : activeCollections.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        textAlign: "center",
                        padding: "18px 0",
                        color: "var(--text3)",
                        fontSize: 12,
                      }}
                    >
                      No active collections
                    </td>
                  </tr>
                ) : (
                  activeCollections.map((c) => (
                    <tr key={c.id}>
                      <td className="mono t-flame fs11">{c.id}</td>
                      <td className="fs12 fw6">{c.customer}</td>
                      <td className="fs11 tc2">
                        {c.date ? fmt.date(c.date) : "—"}
                      </td>
                      <td>
                        <Badge status={c.status} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </PermBlur>
      </div>

      {/* ── Allocation Summary — gated on ASSET:READ (same as donut) ── */}
      <PermBlur allowed={perm(canViewAssets)}>
        <div className="panel dash-inventory-panel">
          <div className="ph" style={{ flexWrap: "wrap", gap: 8 }}>
            <span className="ph-title">
              <i className="bi bi-receipt" /> Allocation Summary
            </span>

            <div
              className="ph-controls"
              style={{
                display: "flex",
                flexWrap: "wrap",
                background: "var(--surface1)",
                gap: 8,
                alignItems: "center",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                }}
              >
                Branch
                <select
                  value={selectedAllocationBranch ?? ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    const branchId = value ? parseInt(value, 10) : null;
                    setSelectedAllocationBranch(branchId);
                    setSelectedAllocationWarehouse(null);
                  }}
                  style={{
                    minWidth: 180,
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px solid #ccc",
                    background: "var(--surface1)",
                    color: "var(--text1)",
                  }}
                >
                  <option value="">Select branch</option>
                  {allocationBranches.map((branch) => {
                    const bid =
                      branch.branch_id ?? branch.id ?? branch.branchId;
                    const name =
                      branch.branch_name ??
                      branch.name ??
                      branch.display_name ??
                      "Branch";
                    return (
                      <option key={bid} value={bid}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                }}
              >
                Warehouse
                <select
                  value={selectedAllocationWarehouse ?? ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedAllocationWarehouse(
                      value ? parseInt(value, 10) : null,
                    );
                  }}
                  style={{
                    minWidth: 180,
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px solid #ccc",
                    background: "var(--surface1)",
                    color: "var(--text1)",
                  }}
                  disabled={
                    !selectedAllocationBranch ||
                    allocationWarehouses.length === 0
                  }
                >
                  <option value="">All warehouses</option>
                  {allocationWarehouses.map((warehouse) => {
                    const wid =
                      warehouse.warehouse_id ??
                      warehouse.id ??
                      warehouse.warehouseId;
                    const name =
                      warehouse.warehouse_name ??
                      warehouse.name ??
                      warehouse.display_name ??
                      "Warehouse";
                    return (
                      <option key={wid} value={wid}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </label>
            </div>
          </div>

          <div className="pb" style={{ padding: 0 }}>
            {allocationLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "24px 0",
                  color: "var(--text3)",
                  fontSize: 12,
                }}
              >
                Loading…
              </div>
            ) : allocationRows.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "24px 0",
                  color: "var(--text3)",
                  fontSize: 12,
                }}
              >
                No allocation data for this period
              </div>
            ) : (
              <table className="tbl" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Description</th>
                    <th style={{ textAlign: "left" }}>Qty</th>
                    <th>Warehouse</th>
                    <th>Branch</th>
                  </tr>
                </thead>
                <tbody>
                  {allocationRows.map((r, idx) => (
                    <tr key={`${r.asset_type_code}-${idx}`}>
                      <td
                        className="mono fs11 t-flame"
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {r.asset_type_code ?? "—"}
                      </td>
                      <td className="fs12" style={{ maxWidth: 220 }}>
                        {r.asset_description ?? "—"}
                      </td>
                      <td
                        className="mono fw6"
                        style={{ textAlign: "left", marginLeft: 4 }}
                      >
                        {r.quantity ?? r.qty ?? "—"}
                      </td>
                      <td className="fs11 tc2">{r.warehouse_name ?? "—"}</td>
                      <td className="fs11 tc2">{r.branch_name ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </PermBlur>
    </div>
  );
}

export default DashboardPage;